import { useMemo } from 'react';
import type { EditorSelection } from '@peruselab/structure-editor/domain/editor';
import { atomStereoValues, type BondStereoValue } from '@peruselab/structure-editor/domain/documentSelectors';
import type { DrawingDocument } from '../generated/drawing-contract';

/**
 * Selection-derived single-target state for useDrawingDocument.ts (roadmap item 35): "what does
 * the property palette / bonds rail act on" -- pure derivations of `document` + `selection` (+
 * `stereoBonds`, a projection owned by useDrawingProjections), no state or effects of its own.
 */
export function useSelectionTargets(
  document: DrawingDocument,
  selection: EditorSelection,
  stereoBonds: Readonly<Record<string, BondStereoValue>>
) {
  const selectedTransformAtomIds = useMemo(() => {
    const ids = new Set(selection.atomIds);
    document.structure.graph.edges
      .filter((edge) => selection.bondIds.includes(edge.id))
      .forEach((edge) => {
        ids.add(edge.source);
        ids.add(edge.target);
      });
    return [...ids];
  }, [document.structure.graph.edges, selection]);

  const singleAtomId =
    selection.atomIds.length === 1 && selection.bondIds.length === 0 ? selection.atomIds[0] : undefined;
  const singleBondId =
    selection.bondIds.length === 1 && selection.atomIds.length === 0 ? selection.bondIds[0] : undefined;
  const selectedAtom = singleAtomId ? document.structure.atoms[singleAtomId] : undefined;
  const selectedFormalCharge =
    singleAtomId && document.structure.formalCharges[singleAtomId] !== undefined
      ? { formalCharge: document.structure.formalCharges[singleAtomId]! }
      : undefined;
  const selectedResonanceCharge = singleAtomId
    ? document.depiction.atomLabels[singleAtomId]?.resonanceCharge
    : undefined;
  const selectedAtomStereo = singleAtomId ? atomStereoValues(document)[singleAtomId] : undefined;

  // The bond the left-rail Bonds group acts on: the selected bond when exactly one is selected,
  // otherwise the most recently drawn bond -- so the quick-apply buttons work right after a draw
  // without a select step (edges are appended, so the last one is the newest).
  const lastBondId = document.structure.graph.edges.at(-1)?.id;
  const activeBondId = singleBondId ?? lastBondId;
  const activeBond = activeBondId ? document.structure.bonds[activeBondId] : undefined;
  const activeBondStereo = activeBondId ? stereoBonds[activeBondId] : undefined;

  return {
    selectedTransformAtomIds,
    singleAtomId,
    singleBondId,
    selectedAtom,
    selectedFormalCharge,
    selectedResonanceCharge,
    selectedAtomStereo,
    activeBondId,
    activeBond,
    activeBondStereo
  };
}
